import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Box } from '@mui/material'
import FunnelGraph from 'd3-funnel-graph'
import 'd3-funnel-graph/dist/css/funnel-graph.min.css'

import { INTERAX_COLORS } from '@/constants/colors'
import type { LoopCommunication } from './StackedLineChartCenteredVisualization'
import { transformLoopsToFunnelData, type FunnelGraphData } from './funnelGraphDataTransformer'
import SingleLoopBarChart from './SingleLoopBarChart'

export interface FunnelGraphVisualizationProps {
  loops: LoopCommunication[]
  height?: number
  loopLabels?: string[]
  direction?: 'horizontal' | 'vertical'
  showLoopSummary?: boolean
}

const BASE_MARGIN = {
  top: 80, // Increased to allow space for labels
  right: 5, // 5px spacing between loops
  bottom: 40, // Increased to allow space for labels
  left: 70, // Increased to allow space for labels
  text: {
    left: 10,
    top: 0
  }
}

const FunnelGraphVisualization: React.FC<FunnelGraphVisualizationProps> = ({
  loops,
  height = 320,
  loopLabels,
  direction = 'horizontal'
}) => {
  // For single loop, use custom bar chart instead of funnel
  // This check happens before hooks to avoid hook order issues
  const isSingleLoop = loops.length === 1

  const containerRef = useRef<HTMLDivElement | null>(null)
  const graphInstanceRef = useRef<FunnelGraph | null>(null)

  // For multiple loops, use the funnel graph
  const processedLoops = useMemo(() => {
    return loops
  }, [loops])

  const computed = useMemo<{
    graphData: FunnelGraphData | null
    loopSummaries: Array<{ label: string; total: number }>
  }>(() => {
    // Transform loops to funnel graph data format
    const graphData = transformLoopsToFunnelData(processedLoops, { loopLabels })

    if (!graphData) {
      return {
        graphData: null,
        loopSummaries: []
      }
    }

    // Calculate loop summaries (totals for each loop)
    const loopSummaries = processedLoops.map((loop, index) => ({
      label: graphData.labels[index] || `Loop ${loop.loopIndex ?? index + 1}`,
      total: loop.players.reduce((sum, player) => sum + (player.talkedTotal ?? 0), 0)
    }))

    return {
      graphData,
      loopSummaries
    }
  }, [processedLoops, loopLabels])

  const graphData = computed.graphData
  // Extract player colors for legend
  const legendItems = useMemo(() => {
    if (!graphData || !graphData.subLabels || !graphData.colors) {
      return []
    }

    return graphData.subLabels.map((playerName, index) => {
      // Get the color for this player (first color of the gradient array)
      const playerColorArray = Array.isArray(graphData.colors[index]) ? (graphData.colors[index] as string[]) : []
      const playerColor = playerColorArray[0] || INTERAX_COLORS.white

      return {
        name: playerName,
        color: playerColor
      }
    })
  }, [graphData])

  const formatValue = useCallback(({ value }: { value: number | string }): string => `${Math.round(Number(value))}`, [])
  const formatTooltip = useCallback(({ value }: { value: number | string }): string => {
    const formattedValue = Math.round(Number(value))

    return String(formattedValue)
  }, [])

  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    if (!graphData) {
      graphInstanceRef.current?.destroy()
      graphInstanceRef.current = null
      return
    }

    const createGraph = () => {
      if (!containerRef.current) return

      graphInstanceRef.current?.destroy()

      const graph = new FunnelGraph({
        container: containerRef.current,
        data: {
          labels: graphData.labels,
          subLabels: graphData.subLabels,
          values: graphData.values,
          colors: graphData.colors
        },
        direction,
        gradientDirection: direction === 'vertical' ? 'vertical' : 'horizontal',
        height,
        width: containerRef.current.clientWidth || undefined,
        displayPercent: false, // Disable percentage display - we're showing counts, not percentages
        tooltip: true,
        tooltipLabel: false, // Disable auto-label to avoid duplication
        details: true, // Enable details to show subLabels and values
        subLabelValue: 'raw', // Use raw values for subLabels
        margin: BASE_MARGIN,
        responsive: true,
        responsiveWidth: true,
        responsiveHeight: false,
        resize: false,
        format: {
          value: formatValue, // Format value with % sign
          tooltip: formatTooltip
        }
      })

      graph.draw()
      graphInstanceRef.current = graph

      // Add percentage labels to each segment (player row) for each loop
      if (graphData && graphData.subLabels && graphData.subLabels.length > 0 && graphData.labels) {
        setTimeout(() => {
          const container = containerRef.current
          if (!container) return

          const svg = container.querySelector('svg')
          if (!svg) return

          const graphGroup = svg.querySelector('g[id*="_graph"]')
          if (!graphGroup) return

          // Find all segment paths (one per player)
          const paths = svg.querySelectorAll('path[data-info]')
          const numLoops = graphData.labels.length

          paths.forEach((path, playerIndex) => {
            try {
              const dataInfo = path.getAttribute('data-info')
              if (!dataInfo) return

              const data = JSON.parse(dataInfo)
              const values = data.values || [] // Array of values for this player across loops

              // Get the path's bounding box
              const svgPath = path as SVGPathElement
              const bbox = svgPath.getBBox()
              const segmentWidth = bbox.width
              const segmentHeight = bbox.height

              // Calculate position for each loop label
              // For horizontal funnel, divide the width by number of loops
              const loopWidth = segmentWidth / numLoops

              // Remove existing labels for this segment
              const existingLabels = graphGroup.querySelectorAll(`g[data-segment-labels="${playerIndex}"]`)
              existingLabels.forEach((label) => label.remove())

              // Create a group for this segment's labels
              const labelGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
              labelGroup.setAttribute('data-segment-labels', String(playerIndex))

              // Add a label for each loop
              for (let i = 0; i < values.length; i++) {
                const value = values[i]
                const displayValue = Math.round(Number(value))

                // Calculate x position for this loop (center of loop section)
                const loopCenterX = bbox.x + i * loopWidth + loopWidth / 2
                const labelY = bbox.y + segmentHeight / 2

                // Create background rectangle
                const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
                const textPadding = 6
                const textHeight = 14
                const rectWidth = 35
                const rectHeight = textHeight + textPadding

                rect.setAttribute('x', String(loopCenterX - rectWidth / 2))
                rect.setAttribute('y', String(labelY - rectHeight / 2))
                rect.setAttribute('width', String(rectWidth))
                rect.setAttribute('height', String(rectHeight))
                rect.setAttribute('rx', '4')
                rect.setAttribute('ry', '4')
                rect.setAttribute('fill', 'rgba(0, 0, 0, 0.6)')
                rect.setAttribute('data-segment-bg', `${playerIndex}-${i}`)

                // Create text element
                const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
                textElement.setAttribute('x', String(loopCenterX))
                textElement.setAttribute('y', String(labelY))
                textElement.setAttribute('text-anchor', 'middle')
                textElement.setAttribute('dominant-baseline', 'middle')
                textElement.setAttribute('data-segment-label', `${playerIndex}-${i}`)
                textElement.setAttribute('class', 'segment-percentage-label')
                textElement.textContent = `${displayValue}`

                // Style the text
                textElement.setAttribute('fill', INTERAX_COLORS.white)
                textElement.setAttribute('font-size', '11px')
                textElement.setAttribute('font-weight', '500')
                textElement.setAttribute('font-family', 'Lexend Deca, system-ui, sans-serif')
                textElement.setAttribute('pointer-events', 'none')

                // Add background and text to group
                labelGroup.appendChild(rect)
                labelGroup.appendChild(textElement)
              }

              // Add the label group to the graph
              graphGroup.appendChild(labelGroup)
            } catch (error) {
              console.warn('Error adding segment labels:', error)
            }
          })

          // Hide percentage labels that appear below loop names (83.2%, 100%, etc.)
          // These are the .label__value elements that are positioned below .label__title
          const labelGroups = svg.querySelectorAll('.label__group')
          labelGroups.forEach((group) => {
            const groupElement = group as SVGGElement
            // Skip if already hidden (for single loop case)
            if (groupElement.getAttribute('display') === 'none') {
              return
            }
            const title = groupElement.querySelector('.label__title')
            const value = groupElement.querySelector('.label__value')

            if (title && value) {
              const titleBbox = (title as SVGTextElement).getBBox()
              const valueBbox = (value as SVGTextElement).getBBox()

              // If the value label is below the title label, hide it
              // (these are the bottom percentages like 83.2%, 100%)
              if (valueBbox.y > titleBbox.y) {
                ;(value as SVGTextElement).setAttribute('display', 'none')
              }
            }
          })
        }, 100) // Small delay to ensure SVG is fully rendered
      }
    }

    createGraph()

    // Handle window resize for better responsiveness
    const handleResize = () => {
      if (containerRef.current && graphInstanceRef.current) {
        createGraph()
      }
    }

    const resizeObserver = new ResizeObserver(handleResize)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
      graphInstanceRef.current?.destroy()
      graphInstanceRef.current = null
    }
  }, [graphData, height, direction, formatValue, formatTooltip])

  // For single loop, render custom bar chart (after all hooks)
  if (isSingleLoop) {
    return <SingleLoopBarChart loops={loops} height={height} loopLabels={loopLabels} direction={direction} />
  }

  if (!graphData) {
    return null
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        '& .d3-funnel-js': {
          fontFamily: 'Lexend Deca, system-ui, sans-serif',
          margin: 0
        },
        '& .d3-funnel-js .label__group .label__value': {
          fill: INTERAX_COLORS.white,
          fontWeight: 600,
          fontSize: '14px'

          // Note: Bottom loop totals (89%, 107%) are hidden via JavaScript after render
        },
        '& .d3-funnel-js .label__percentage': {
          fill: INTERAX_COLORS.white,
          fontWeight: 600,
          fontSize: '14px',
          display: 'none'

          // Note: Bottom loop totals (89%, 107%) are hidden via JavaScript after render
        },

        '& .d3-funnel-js .label__group .label__title': {
          fill: INTERAX_COLORS.green,
          fontWeight: 500,
          fontSize: '12px'
        },
        '& .d3-funnel-js .sub-label': {
          fill: INTERAX_COLORS.white,
          fontSize: '11px',
          opacity: 0.9
        },
        '& .d3-funnel-js .funnel__segment-label': {
          fill: INTERAX_COLORS.white,
          fontSize: '10px',
          fontWeight: 500
        },
        '& .d3-funnel-js .divider': {
          stroke: INTERAX_COLORS.heritageBlue,
          strokeWidth: 5,
          opacity: 0.5
        },
        '& .d3-funnel-js .segment-label': {
          fill: INTERAX_COLORS.white,
          fontSize: '11px',
          fontWeight: 500,
          fontFamily: 'Lexend Deca, system-ui, sans-serif'
        },
        '& .segment-percentage-label': {
          fill: INTERAX_COLORS.white,
          fontSize: '11px',
          fontWeight: 500,
          fontFamily: 'Lexend Deca, system-ui, sans-serif',
          pointerEvents: 'none'
        },
        '& .d3-funnel-js-tooltip': {
          backgroundColor: INTERAX_COLORS.offBlack,
          borderColor: INTERAX_COLORS.white25,
          color: INTERAX_COLORS.white,
          fontFamily: 'Lexend Deca, system-ui, sans-serif',
          zIndex: 9999,
          pointerEvents: 'none',
          position: 'absolute',
          display: 'block !important',
          visibility: 'visible !important',
          opacity: '1 !important'
        }
      }}
    >
      {/* Legend: Color and Player Name - Compact */}
      {legendItems.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: { xs: 1, sm: 1, md: 1 },
            paddingY: 0.5,
            paddingLeft: `${BASE_MARGIN.left}px` // Align legend with graph start position
          }}
        >
          {legendItems.map((item, index) => (
            <Box
              key={`legend-${item.name}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '2px',
                  backgroundColor: item.color,
                  flexShrink: 0
                }}
              />
              <Box
                component="span"
                sx={{
                  fontSize: '0.75rem',
                  color: INTERAX_COLORS.white,
                  fontFamily: 'Lexend Deca, system-ui, sans-serif',
                  lineHeight: 1
                }}
              >
                {item.name}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height,
          position: 'relative',
          '& svg': {
            overflow: 'visible'
          },
          '& .d3-funnel-js': {
            position: 'relative'
          }
        }}
      />
    </Box>
  )
}

// Comparison function for React.memo to prevent unnecessary re-renders
// Compares loops arrays by value, not reference
const areLoopsEqual = (prevProps: FunnelGraphVisualizationProps, nextProps: FunnelGraphVisualizationProps): boolean => {
  // Compare primitive props
  if (
    prevProps.height !== nextProps.height ||
    prevProps.direction !== nextProps.direction ||
    prevProps.showLoopSummary !== nextProps.showLoopSummary
  ) {
    return false
  }

  // Compare loopLabels arrays
  if (prevProps.loopLabels?.length !== nextProps.loopLabels?.length) {
    return false
  }
  if (prevProps.loopLabels && nextProps.loopLabels) {
    if (prevProps.loopLabels.some((label, i) => label !== nextProps.loopLabels![i])) {
      return false
    }
  }

  // Compare loops arrays by value
  if (prevProps.loops.length !== nextProps.loops.length) {
    return false
  }

  for (let i = 0; i < prevProps.loops.length; i++) {
    const prevLoop = prevProps.loops[i]
    const nextLoop = nextProps.loops[i]

    if (prevLoop.loopIndex !== nextLoop.loopIndex) {
      return false
    }

    if (prevLoop.players.length !== nextLoop.players.length) {
      return false
    }

    for (let j = 0; j < prevLoop.players.length; j++) {
      const prevPlayer = prevLoop.players[j]
      const nextPlayer = nextLoop.players[j]

      if (
        prevPlayer.playerId !== nextPlayer.playerId ||
        prevPlayer.displayName !== nextPlayer.displayName ||
        prevPlayer.talkedTotal !== nextPlayer.talkedTotal
      ) {
        return false
      }
    }
  }

  return true
}

export default React.memo(FunnelGraphVisualization, areLoopsEqual)
